// client/src/pages/csgo/Gambling.js

import React, { useEffect, useState } from 'react';
import { Alert, Spinner, Card, Table, Form, Row, Col, Button, Badge } from 'react-bootstrap';
import { FaPlus, FaTrash, FaDiceFive } from 'react-icons/fa';
import useCsgo from '../../hooks/useCsgo';

const usd = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v || 0));

const EMPTY = {
  type: 'DEPOSIT',
  amount: '',
  itemName: '',
  site: 'Skinclub',
  date: new Date().toISOString().slice(0, 10)
};

const StatCard = ({ label, value, tone }) => (
  <Col md={3} xs={6}>
    <Card className="border-0 shadow-sm bg-white h-100">
      <Card.Body className="p-3">
        <div className="text-xs text-muted text-uppercase fw-bold mb-1">{label}</div>
        <div className={`h5 fw-bold mb-0 ${tone || ''}`}>{value}</div>
      </Card.Body>
    </Card>
  </Col>
);

function Gambling() {
  const { gambling, gamblingStats, loading, error, fetchGambling, createGambling, deleteGambling } =
    useCsgo();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    fetchGambling();
  }, [fetchGambling]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount) return;
    try {
      await createGambling(form);
      setForm({ ...EMPTY, date: form.date });
    } catch {
      // erreur remontée par le hook
    }
  };

  const net = Number(gamblingStats?.netProfit || 0);
  const roi = Number(gamblingStats?.roi || 0);

  return (
    <div className="animate-fade-in pb-5">
      <h1 className="h4 mb-4 fw-bold d-flex align-items-center">
        <FaDiceFive className="me-2 text-accent" /> Gambling
      </h1>

      {error && <Alert variant="danger">{error}</Alert>}

      {gamblingStats && (
        <Row className="g-3 mb-4">
          <StatCard label="Dépôts cash" value={usd(gamblingStats.totalDeposits)} />
          <StatCard label="Valeur gains" value={usd(gamblingStats.totalWins)} tone="text-success" />
          <StatCard
            label="Profit net"
            value={`${net >= 0 ? '+' : ''}${usd(net)}`}
            tone={net >= 0 ? 'text-success' : 'text-danger'}
          />
          <StatCard
            label="ROI"
            value={`${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`}
            tone={roi >= 0 ? 'text-success' : 'text-danger'}
          />
        </Row>
      )}

      <Card className="border-0 shadow-sm bg-white mb-4">
        <Card.Body className="p-4">
          <Card.Title className="h6 fw-bold mb-3">Nouvelle entrée</Card.Title>
          <Form onSubmit={handleSubmit}>
            <Row className="g-2">
              <Col md={2}>
                <Form.Select size="sm" value={form.type} onChange={set('type')}>
                  <option value="DEPOSIT">Dépôt</option>
                  <option value="WIN">Gain</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.01"
                  placeholder="Montant ($)"
                  value={form.amount}
                  onChange={set('amount')}
                  required
                />
              </Col>
              <Col md={3}>
                <Form.Control
                  size="sm"
                  placeholder="Item gagné (optionnel)"
                  value={form.itemName}
                  onChange={set('itemName')}
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  size="sm"
                  placeholder="Site"
                  value={form.site}
                  onChange={set('site')}
                />
              </Col>
              <Col md={2}>
                <Form.Control size="sm" type="date" value={form.date} onChange={set('date')} />
              </Col>
              <Col md={1}>
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  disabled={loading.saveGambling}
                  className="w-100"
                >
                  <FaPlus />
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm bg-white">
        <Card.Body className="p-4">
          {loading.gambling && !gambling.length ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table borderless size="sm" className="align-middle mb-0">
                <thead className="text-muted text-xs text-uppercase border-bottom">
                  <tr>
                    <th className="pb-2">Date</th>
                    <th className="pb-2">Type</th>
                    <th className="pb-2">Item</th>
                    <th className="pb-2">Site</th>
                    <th className="pb-2 text-end">Montant</th>
                    <th className="pb-2 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {gambling.map((g) => (
                    <tr key={g.id}>
                      <td className="py-2 text-sm">
                        {new Date(g.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-2">
                        <Badge bg={g.type === 'WIN' ? 'success' : 'secondary'}>
                          {g.type === 'WIN' ? 'Gain' : 'Dépôt'}
                        </Badge>
                      </td>
                      <td className="py-2 text-sm">{g.itemName || '—'}</td>
                      <td className="py-2 text-sm text-muted">{g.site}</td>
                      <td className="py-2 text-end text-sm fw-bold">{usd(g.amount)}</td>
                      <td className="py-2 text-end">
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => deleteGambling(g.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!gambling.length && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        Aucune entrée gambling.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default Gambling;
